import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '../App';
import { profileAPI } from '../lib/api';
import { apiErrorMessage } from '../lib/apiErrors';
import { t } from '../lib/i18n';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { STEPS, EMPTY_PROFILE, normalizeProfile } from '../components/onboarding/constants';
import { OnboardingStepper } from '../components/onboarding/OnboardingStepper';
import { OnboardingNav } from '../components/onboarding/OnboardingNav';
import { StepIdentity, StepMorphology, StepGoals, StepStyle, StepSummary } from '../components/onboarding/steps';
import { PrivateSeo } from '../components/seo/PrivateSeo';

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const { updateUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const update = (key, value) => setProfile(prev => ({ ...prev, [key]: value }));
  const toggleGoal = (goal) => setProfile(prev => ({
    ...prev,
    goals: prev.goals.includes(goal) ? prev.goals.filter(g => g !== goal) : [...prev.goals, goal],
  }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const cleanProfile = normalizeProfile(profile);
      await profileAPI.update(cleanProfile);
      updateUser({ onboarding_complete: true, profile: cleanProfile });
      toast.success(language === 'fr' ? 'Profil cr\u00e9\u00e9 avec succ\u00e8s!' : 'Profile created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(apiErrorMessage(err, language, language === 'fr' ? 'Erreur lors de la sauvegarde du profil' : 'Error saving profile'));
    } finally {
      setLoading(false);
    }
  };

  const stepProps = { profile, update, toggleGoal, language };
  const stepViews = [
    <StepIdentity key="identity" {...stepProps} />,
    <StepMorphology key="morphology" {...stepProps} />,
    <StepGoals key="goals" {...stepProps} />,
    <StepStyle key="style" {...stepProps} />,
    <StepSummary key="summary" {...stepProps} />,
  ];

  return (
    <div className="min-h-screen bg-[var(--lux-ink)] flex flex-col items-center justify-center px-4 py-8" data-testid="onboarding-page">
      <PrivateSeo titleKey="onboardingTitle" />
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/logo.jpeg" alt="PrimeFace" className="w-10 h-10 rounded-xl object-cover border border-lux-gold/30" />
            <h1 className="font-display text-3xl text-white">{t('onboardingTitle', language)}</h1>
          </div>
          <p className="text-white/50 text-sm">{t('onboardingSubtitle', language)}</p>
        </div>

        <OnboardingStepper step={step} language={language} />

        <Card className="bg-lux-panel/90 border-white/[0.08] shadow-[0_12px_32px_rgba(0,0,0,0.55)]">
          <CardContent className="pt-6">
            {stepViews[step]}
            <OnboardingNav
              step={step}
              totalSteps={STEPS.length}
              loading={loading}
              language={language}
              onBack={() => setStep(s => Math.max(0, s - 1))}
              onNext={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
              onSubmit={handleSubmit}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
